import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest } from "../../../core/common/api";
import { canManageUsers } from "../../../core/common/adminPermissions";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  password: "",
  sponsorId: "",
};

const UserAdd = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [needsSponsor, setNeedsSponsor] = useState(true);
  const [checkingNetwork, setCheckingNetwork] = useState(true);

  useEffect(() => {
    if (!canManageUsers()) {
      navigate("/users", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const loadNetwork = async () => {
      setCheckingNetwork(true);
      try {
        const res = await apiRequest("/users?limit=1", "GET");
        const total = Number(res?.stats?.totalUsers ?? res?.pagination?.total ?? 0);
        setNeedsSponsor(total > 0);
      } catch {
        setNeedsSponsor(true);
      } finally {
        setCheckingNetwork(false);
      }
    };

    loadNetwork();
  }, []);

  const updateForm = (updates: Partial<typeof emptyForm>) => {
    setForm((current) => ({ ...current, ...updates }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      Swal.fire("Validation Error", "Name, email and phone are required", "error");
      return;
    }

    if (needsSponsor && !form.sponsorId.trim()) {
      Swal.fire("Validation Error", "Sponsor ID is required", "error");
      return;
    }

    setSaving(true);

    try {
      const result = await apiRequest("/users", "POST", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password.trim(),
        sponsorId: needsSponsor ? form.sponsorId.trim() : "",
      });

      if (!result.success) {
        throw new Error(result.message || "Unable to create user");
      }

      await Swal.fire({
        title: "User Created",
        html: `<p>Member ID: <strong>${result.user?.memberId || "—"}</strong></p>
               <p>Password: <strong>${result.password || "—"}</strong></p>`,
        icon: "success",
      });

      navigate(result.user?._id ? `/users/${result.user._id}` : "/users");
    } catch (error: any) {
      Swal.fire("Create Failed", error.message || "Unable to create user", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/users" className="btn-sm btn-outline back-link">
            ← Back to All Users
          </Link>
          <div className="page-eyebrow">User Management</div>
          <h1 className="page-title">
            Add <em>User</em>
          </h1>
          <div className="page-sub">
            {needsSponsor
              ? "Create a registered member. They stay inactive until their first paid or delivered order generates a sale ID."
              : "First registered member does not need a sponsor. They stay inactive until their first activating order."}
          </div>
        </div>
        <div className="page-hdr-right">
          <button
            type="submit"
            form="user-add-form"
            className="btn-sm btn-primary"
            disabled={saving || checkingNetwork}
          >
            {saving ? "Creating..." : "Create User"}
          </button>
        </div>
      </div>

      <form id="user-add-form" onSubmit={handleSubmit} className="page-stack">
        <section className="table-card">
          <div className="table-head">
            <h2>Account Details</h2>
            <p>Name, contact and sponsor. Password is optional — a password is generated if left blank.</p>
          </div>
          <div className="admin-card-body admin-form-grid">
            <div className="form-field">
              <label className="modal-label">Full Name</label>
              <input
                className="admin-input"
                value={form.name}
                onChange={(event) => updateForm({ name: event.target.value })}
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Email Address</label>
              <input
                type="email"
                className="admin-input"
                value={form.email}
                onChange={(event) => updateForm({ email: event.target.value })}
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Phone Number</label>
              <input
                className="admin-input"
                value={form.phone}
                onChange={(event) => updateForm({ phone: event.target.value })}
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Password (optional)</label>
              <input
                className="admin-input"
                value={form.password}
                onChange={(event) => updateForm({ password: event.target.value })}
                placeholder="Leave blank to auto-generate"
                minLength={6}
              />
            </div>
            {needsSponsor && (
              <div className="form-field">
                <label className="modal-label">Sponsor ID</label>
                <input
                  className="admin-input"
                  value={form.sponsorId}
                  onChange={(event) => updateForm({ sponsorId: event.target.value })}
                  placeholder="RW960001"
                  required
                />
              </div>
            )}
          </div>
        </section>

        <div className="detail-footer">
          <Link to="/users" className="btn-sm btn-outline">
            Cancel
          </Link>
          <div className="detail-footer-right">
            <button
              type="submit"
              className="btn-sm btn-primary"
              disabled={saving || checkingNetwork}
            >
              {saving ? "Creating..." : "Create User"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UserAdd;
